import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Optional, Inject, APP_INITIALIZER, NgModule } from '@angular/core';
import { DOCUMENT } from '@angular/common';

const COOKIE_OPTIONS = new InjectionToken('COOKIE_OPTIONS');

const DATE_MAX_EXPIRES = new Date('Fri, 31 Dec 9999 23:59:59 GMT');
const DEFAULT_COOKIE_OPTIONS = {
    path: '/',
    expires: Infinity
};
class CookieFactory {
}
class CookieService {
    constructor(cookieOptions, cookieFactory) {
        this.cookieFactory = cookieFactory;
        this.cookieOptions = cookieOptions || DEFAULT_COOKIE_OPTIONS;
    }
    /**
     * Returns an integer representing the number of cookie items.
     */
    get length() {
        return this.keys().length;
    }
    /**
     * Transform to expires Date
     */
    static getExpiresDate(expires) {
        if (!expires) {
            return null;
        }
        switch (expires.constructor) {
            case Number:
                return expires === Infinity ? DATE_MAX_EXPIRES : new Date(expires * 1000 + Date.now());
            case String:
                return new Date(expires);
            default:
                return expires;
        }
    }
    /**
     * Get all cookies in object format.
     */
    getAll() {
        return this.cookieFactory.getAll();
    }
    /**
     * Read a cookie. If the cookie doesn't exist a null value will be returned.
     */
    getItem(key) {
        return this.getAll()[key] || null;
    }
    /**
     * Check whether a cookie exists in the current position.
     */
    hasItem(key) {
        return Object.prototype.hasOwnProperty.call(this.getAll(), key);
    }
    /**
     * Return all cookie names.
     */
    keys() {
        return Object.keys(this.getAll());
    }
    /**
     * Return the cookie name at a index.
     */
    key(index) {
        return this.keys()[index] || null;
    }
    /**
     * Add that cookie to the storage, or update that cookie's value if it already exists.
     */
    setItem(key, data, options) {
        if (!key) {
            return;
        }
        // Merge options
        options = Object.assign({}, this.cookieOptions, options || {});
        // Remove data
        if (!data) {
            options.expires = 'Thu, 01 Jan 1970 00:00:00 GMT';
        }
        // Convert expires to Date
        if (options.expires) {
            options.expires = CookieService.getExpiresDate(options.expires);
        }
        this.cookieFactory.save(key, data, options);
    }
    /**
     * Delete a cookie.
     */
    removeItem(key, options) {
        return this.setItem(key, null, options);
    }
    /**
     * Remove all cookie.
     */
    clear(options) {
        this.keys().forEach(key => {
            this.removeItem(key, options);
        });
    }
}
CookieService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.0", ngImport: i0, type: CookieService, deps: [{ token: COOKIE_OPTIONS, optional: true }, { token: CookieFactory }], target: i0.ɵɵFactoryTarget.Injectable });
CookieService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.2.0", ngImport: i0, type: CookieService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.0", ngImport: i0, type: CookieService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [COOKIE_OPTIONS]
                }] }, { type: CookieFactory }]; } });

/**
 * Convert cookies string to object
 * @param cookiesStr
 */
function cookiesStrToObj(cookiesStr) {
    const cookies = {};
    if (!cookiesStr) {
        return cookies;
    }
    cookiesStr.split('; ').forEach(cookie => {
        const cookieSplited = cookie.split('=');
        cookies[decodeURIComponent(cookieSplited[0])] = decodeURIComponent(cookieSplited[1]);
    });
    return cookies;
}

class BrowserCookieFactory {
    constructor(document) {
        this.document = document;
        this.lastCookies = '';
        this.cookies = {};
    }
    getAll() {
        const cookiesStr = this.document.cookie;
        if (this.lastCookies !== cookiesStr) {
            this.lastCookies = cookiesStr;
            this.cookies = cookiesStrToObj(cookiesStr);
        }
        return this.cookies;
    }
    save(key, data, options) {
        this.document.cookie = `${encodeURIComponent(key)}=${data ? encodeURIComponent(data) : ''}${options.expires ? `; expires=${options.expires.toUTCString()}` : ''}${options.domain ? `; domain=${options.domain}` : ''}${options.path ? `; path=${options.path}` : ''}${options.secure ? '; secure' : ''}`;
    }
}
BrowserCookieFactory.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.0", ngImport: i0, type: BrowserCookieFactory, deps: [{ token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Injectable });
BrowserCookieFactory.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.2.0", ngImport: i0, type: BrowserCookieFactory });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.0", ngImport: i0, type: BrowserCookieFactory, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }]; } });

const COOKIE_DECORATOR_DATA = {};
/**
 * Property Decorator to Get / Set cookie
 */
function Cookie(name, options) {
    return function (target, key) {
        let _value;
        if (delete target[key]) {
            const cookieName = name || key;
            Object.defineProperty(target, key, {
                get: function () {
                    if (COOKIE_DECORATOR_DATA.cookieService) {
                        const cookieValue = COOKIE_DECORATOR_DATA.cookieService.getItem(cookieName);
                        try {
                            return JSON.parse(cookieValue);
                        }
                        catch (e) {
                            return cookieValue;
                        }
                    }
                    else {
                        return _value;
                    }
                },
                set: function (value) {
                    if (COOKIE_DECORATOR_DATA.cookieService) {
                        COOKIE_DECORATOR_DATA.cookieService.setItem(cookieName, typeof value === 'string' ? value : JSON.stringify(value), options);
                    }
                    else {
                        _value = value;
                    }
                },
                enumerable: true,
                configurable: true
            });
        }
    };
}

function setupCookieDecorator(cookieService) {
    COOKIE_DECORATOR_DATA.cookieService = cookieService;
    return () => null;
}
function newCookieFactory(document) {
    return new BrowserCookieFactory(document);
}
class CookieModule {
    /**
     * In root module to provide the CookieService & CookieOptions
     */
    static forRoot(cookieOptions) {
        return {
            ngModule: CookieModule,
            providers: [
                {
                    provide: COOKIE_OPTIONS,
                    useValue: cookieOptions
                },
                {
                    provide: CookieFactory,
                    useFactory: newCookieFactory,
                    deps: [DOCUMENT]
                },
                CookieService,
                {
                    provide: APP_INITIALIZER,
                    useFactory: setupCookieDecorator,
                    deps: [CookieService],
                    multi: true
                }
            ]
        };
    }
}
CookieModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.0", ngImport: i0, type: CookieModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CookieModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.2.0", ngImport: i0, type: CookieModule });
CookieModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.2.0", ngImport: i0, type: CookieModule });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.0", ngImport: i0, type: CookieModule, decorators: [{
            type: NgModule
        }] });

class ServerCookieFactory {
    constructor(request, response) {
        this.response = response;
        this.cookies = {};
        if (request && request.headers) {
            this.cookies = cookiesStrToObj(request.headers.cookie);
        }
    }
    getAll() {
        return this.cookies;
    }
    save(key, data, options) {
        if (!data) {
            delete this.cookies[key];
            this.response.clearCookie(key, options);
        }
        else {
            this.cookies[key] = data;
            this.response.cookie(key, data, options);
        }
    }
}

/*
 * Public API Surface of cookie
 */

/**
 * Generated bundle index. Do not edit.
 */

export { BrowserCookieFactory, COOKIE_OPTIONS, Cookie, CookieFactory, CookieModule, CookieService, ServerCookieFactory };
